import { formatExportRows, formatMonth, formatName, normalizeSearch } from '../lib/format';
import { withDownloadProgress } from './downloadProgress';
export const REPORT_TIMEZONE = 'Australia/Sydney'
export const CAPACITIES = ['Traffic Controller', 'General Labourer', 'Skilled Labourer', 'Skilled Worker A', 'Skilled Worker B']
const dateParts = new Intl.DateTimeFormat('en-CA', { timeZone: REPORT_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
export const number = value => Number.isFinite(Number(value)) ? Number(value) : 0
export const asDate = value => {
    if (!value) return null
    const raw = value?.toDate ? value.toDate() : typeof value === 'object' && (value.seconds !== undefined || value._seconds !== undefined) ? new Date((value.seconds ?? value._seconds) * 1000) : value
    const date = new Date(raw)
    return Number.isFinite(date.getTime()) ? date : null
}
export const dayKey = value => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
    const date = asDate(value)
    if (!date) return ''
    const parts = dateParts.formatToParts(date)
    return ['year', 'month', 'day'].map(type => parts.find(part => part.type === type).value).join('-')
}
export const monthKey = value => dayKey(value).slice(0, 7)
export const moveMonth = (key, amount) => {
    const [year, month] = key.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1 + amount, 1)).toISOString().slice(0, 7)
}
export const monthLabel = formatMonth
export const hoursFor = row => {
    const start = asDate(row.clockIn)
    const end = asDate(row.clockOut)
    if (!start || !end || end <= start) return 0
    const minutes = Math.trunc((end - start) / 60000) - (row.breakIncludedInPay === false ? Math.max(0, number(row.breakMinutes)) : 0)
    return Math.max(0, Math.round(minutes / 15) / 4)
}
const isCancelled = row => ['cancelled', 'canceled', 'deleted', 'voided'].includes(normalizeSearch(String(row.status || '')))
const unique = rows => [...new Map(rows.map((row, index) => [row.id || `row-${index}`, row])).values()]
export const registration = row => row.registeredAt || row.createdAt || row.created
const splitTypes = value => Array.isArray(value) ? value : String(value || '').split(',').map(type => type.trim()).filter(type => type && type !== '-')

export const capacityAdditions = data => {
    const tracked = data.capacityEvents || []
    const events = tracked.flatMap(event => (event.added || []).map(capacity => ({ workerId: event.workerId, capacity, date: event.date })))
    for (const audit of data.capacityAudit || []) {
        for (const field of audit.changedFields || []) {
            if (field.field !== 'workerType') continue
            const before = splitTypes(field.before)
            const after = splitTypes(field.after)
            // The new event stream and the existing profile audit can record the same save.
            const duplicate = tracked.some(event => event.workerId === audit.workerId && Math.abs(asDate(event.date) - asDate(audit.createdAt)) < 60000 && [...(event.before || [])].sort().join('|') === [...before].sort().join('|') && [...(event.after || [])].sort().join('|') === [...after].sort().join('|'))
            if (!duplicate) for (const capacity of new Set(after.filter(type => !before.includes(type)))) events.push({ workerId: audit.workerId, capacity, date: audit.createdAt })
        }
    }
    return events
}

export const buildAnalytics = (data, selectedMonth, now = new Date()) => {
    const keys = Array.from({ length: 12 }, (_, index) => moveMonth(selectedMonth, index - 11))
    const empty = key => ({ key, traffic: 0, labour: 0, unclassified: 0, hours: 0, allocations: 0, clients: 0, sites: 0, workers: 0, capacities: 0, clientInvoices: 0, workerInvoices: 0, clientInvoiceCount: 0, workerInvoiceCount: 0, assets: new Set() })
    const monthly = new Map(keys.map(key => [key, empty(key)]))
    const selected = monthly.get(selectedMonth)
    const previous = monthly.get(moveMonth(selectedMonth, -1))
    const clients = new Map((data.clients || []).map(row => [row.id, row]))
    const workers = new Map((data.workers || []).map(row => [row.id, row]))
    const assets = new Map((data.assets || []).map(row => [row.id, row]))
    const cancelledAllocations = new Set((data.allocations || []).filter(isCancelled).map(row => row.id))
    const allocations = unique(data.allocations || []).filter(row => !isCancelled(row))
    const allocationMap = new Map(allocations.map(row => [row.id, row]))
    const siteRows = new Map()
    const getSite = (clientId, siteId) => {
        const key = `${clientId || ''}|${siteId || ''}`
        if (!siteRows.has(key)) siteRows.set(key, { key, clientId, siteId, client: clients.get(clientId)?.name || 'Unknown client', site: 'Unknown site', hours: 0, allocations: 0, last: '', next: '', archived: !!clients.get(clientId)?.archived })
        return siteRows.get(key)
    }
    for (const site of data.sites || []) {
        const row = getSite(site.clientId, site.id)
        row.site = site.name || 'Unnamed site'
        row.archived = row.archived || !!site.archived
        row.autoCreated = !!site.autoCreatedForClient
    }
    const today = dayKey(now)
    const assetUsage = new Map()
    for (const allocation of allocations) {
        const date = dayKey(allocation.date)
        if (!date) continue
        const site = getSite(allocation.chosenClient, allocation.chosenSite)
        if (date <= today && (!site.last || date > site.last)) site.last = date
        if (date > today && (!site.next || date < site.next)) site.next = date
        const month = monthly.get(date.slice(0, 7))
        if (!month) continue
        month.allocations += 1
        const assetIds = [...new Set((allocation.chosenAssets || []).map(row => row.asset).filter(Boolean))]
        assetIds.forEach(id => month.assets.add(id))
        if (month !== selected) continue
        site.allocations += 1
        for (const id of assetIds) {
            const asset = assets.get(id) || {}
            if (!assetUsage.has(id)) assetUsage.set(id, { id, name: asset.machineId || asset.regoNo || id, type: asset.assetType || 'Asset', uses: 0, sites: new Set() })
            const entry = assetUsage.get(id)
            entry.uses += 1
            entry.sites.add(site.key)
        }
    }
    let incomplete = 0
    for (const row of unique(data.timesheets || [])) {
        if (isCancelled(row)) continue
        const allocation = allocationMap.get(row.allocationId)
        if (row.allocationId && cancelledAllocations.has(row.allocationId)) continue
        const month = monthly.get(monthKey(row.date || row.clockIn))
        if (!month) continue
        const hours = hoursFor(row)
        if ((!row.clockIn || !row.clockOut) && month === selected) incomplete += 1
        const assignment = allocation?.chosenWorkerAssignments?.find(item => item.workerId === row.userId)?.capacity
        const workerTypes = workers.get(row.userId)?.workerType || row.workerTypes || []
        const capacity = row.workerCapacity || assignment || (workerTypes.length === 1 ? workerTypes[0] : '')
        const category = capacity === 'Traffic Controller' ? 'traffic' : CAPACITIES.slice(1).includes(capacity) ? 'labour' : 'unclassified'
        month[category] += hours
        month.hours += hours
        if (month === selected) getSite(row.chosenClient || allocation?.chosenClient, row.chosenSite || allocation?.chosenSite).hours += hours
    }
    const undated = {}
    for (const name of ['clients', 'sites', 'workers']) {
        undated[name] = 0
        for (const row of data[name] || []) {
            if (name === 'sites' && row.autoCreatedForClient) continue
            const key = monthKey(registration(row))
            if (!key) undated[name] += 1
            const month = monthly.get(key)
            if (month) month[name] += 1
        }
    }
    const additions = capacityAdditions(data)
    const capacityRows = new Map(CAPACITIES.map(capacity => [capacity, { capacity, additions: 0, workers: new Set() }]))
    for (const event of additions) {
        const month = monthly.get(monthKey(event.date))
        if (!month) continue
        month.capacities += 1
        if (month !== selected) continue
        if (!capacityRows.has(event.capacity)) capacityRows.set(event.capacity, { capacity: event.capacity, additions: 0, workers: new Set() })
        capacityRows.get(event.capacity).additions += 1
        capacityRows.get(event.capacity).workers.add(event.workerId)
    }
    const invoiceRows = []
    for (const row of unique(data.invoices || [])) {
        if (isCancelled(row)) continue
        const month = monthly.get(monthKey(row.date))
        if (!month) continue
        const draft = ['draft', 'created', 'modified', ''].includes(normalizeSearch(String(row.status || '')))
        const category = row.client ? 'client' : row.worker || row.workerId ? 'worker' : null
        if (!category) continue
        if (!draft) {
            month[`${category}Invoices`] += number(row.total)
            month[`${category}InvoiceCount`] += 1
        }
        if (month === selected) invoiceRows.push({ ...row, category, draft, name: row.client?.name || formatName(row.worker?.name) || formatName([workers.get(row.workerId)?.firstName, workers.get(row.workerId)?.lastName].filter(Boolean).join(' ')) || 'Unknown worker' })
    }
    const sites = [...siteRows.values()].map(row => ({ ...row, days: row.last ? Math.floor((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${row.last}T00:00:00Z`)) / 86400000) : null }))
    return {
        monthly: [...monthly.values()].map(row => ({ ...row, assets: row.assets.size })),
        selected: { ...selected, assets: selected.assets.size }, previous: { ...previous, assets: previous.assets.size },
        sites: sites.sort((a, b) => b.hours - a.hours || a.client.localeCompare(b.client)),
        followUps: sites.filter(row => !row.archived && !row.autoCreated && !row.next).sort((a, b) => (b.days ?? Infinity) - (a.days ?? Infinity) || a.client.localeCompare(b.client)),
        assets: [...assetUsage.values()].map(row => ({ ...row, sites: row.sites.size })).sort((a, b) => b.uses - a.uses),
        capacities: [...capacityRows.values()].map(row => ({ ...row, workers: row.workers.size })),
        invoices: invoiceRows.sort((a, b) => dayKey(b.date).localeCompare(dayKey(a.date))), incomplete, undated,
    }
}

export const csvContent = rows => '\uFEFF' + rows.map(row => row.map(value => {
    const raw = String(value ?? '')
    const safe = typeof value === 'string' && /^\s*[=+@-]/.test(raw) ? `'${raw}` : raw
    return `"${safe.replace(/"/g, '""')}"`
}).join(',')).join('\r\n')

export const downloadReport = (rows, name) => withDownloadProgress(async () => {
    const url = URL.createObjectURL(new Blob([csvContent(formatExportRows(rows))], { type: 'text/csv;charset=utf-8;' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `${name}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
})
